<script>
import { getCurrentInstance } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import MarkRequiredFieldModal from '@/components/questions/MarkRequiredFieldModal.vue';
import { surveyFieldService } from '@/shared/services/survey-field.service';
import notificationService from '@/shared/services/notification.service';

/**
 * Les champs du questionnaire que l'enquête exige.
 *
 * Bloc a part, et non melange aux questions personnalisees: ces deux listes ne
 * se manipulent pas pareil. Une question personnalisee se cree, se renomme, se
 * supprime; un champ natif est ecrit dans le formulaire et lu par le reste de
 * l'application - on ne peut que decider s'il est obligatoire. Les melanger
 * ferait croire qu'on peut supprimer « Raison sociale ».
 *
 * L'ecran repond a une seule question - « qu'est-ce que le questionnaire
 * exige ? » - et ne montre donc que cela: les champs obligatoires. Le
 * catalogue entier en portera plus de cent quand les six sections seront
 * deployees; le lister au repos noierait la reponse dans les champs dont
 * personne n'a rien a dire. Il se parcourt comme la fenetre de reglage: les
 * sections a gauche avec ce qu'elles exigent sur ce qu'elles portent, la liste
 * a droite.
 *
 * RIEN N'EST ECRIT AVANT « ENREGISTRER ». Chaque geste - marquer, retirer -
 * nourrit un brouillon; la carte affiche ce brouillon, annonce ce qui reste a
 * enregistrer, et n'appelle l'API qu'au moment ou on le lui demande. C'est ce
 * qui rend une reprise possible: on retire une ligne, on en ajoute trois, on
 * change d'avis, et rien de tout cela n'a encore touche le questionnaire que
 * remplissent les enqueteurs. Le prix a payer est de ne pas laisser partir un
 * brouillon en silence, d'ou l'avertissement au depart de la page.
 *
 * La carte se replie, et arrive repliee: l'ecran des questions porte deja sa
 * propre liste. La barre d'enregistrement, elle, reste visible meme repliee -
 * un brouillon ne doit pas pouvoir se cacher derriere un pli.
 *
 * La liste vient de l'API: c'est elle qui declare les champs, leur section, et
 * ceux qu'un verrou protege.
 */
export default {
  name: 'NativeFieldsCard',
  components: { MarkRequiredFieldModal },
  setup() {
    /*
      Le garde de navigation appartient a la route, mais la question - « as-tu
      un brouillon ? » - appartient a cette carte. `proxy` est l'instance
      Options API de ce composant: le garde ne fait que lui passer la main.
    */
    const instance = getCurrentInstance();

    onBeforeRouteLeave(() => instance?.proxy?.confirmLeave());
  },
  data() {
    return {
      fields: [],
      loading: false,
      error: '',
      /**
       * Le brouillon: `cle -> obligatoire`, et rien d'autre. Une cle absente
       * veut dire « pas touche », ce qui n'est pas la meme chose que « remis
       * comme avant ».
       */
      draft: {},
      savingAll: false,
      /** `null` = toutes les sections. */
      step: null,
      pickerOpen: false,
      /** La carte arrive repliee: son en-tete suffit a se situer. */
      open: false,
    };
  },
  computed: {
    /** Le catalogue vu au travers du brouillon: ce que l'ecran doit montrer. */
    drafted() {
      return this.fields.map((field) => {
        const isRequired = this.requiredOf(field);

        return {
          ...field,
          isRequired,
          /** `added`, `removed`, ou rien: ce que l'enregistrement changera. */
          pending:
            isRequired === field.isRequired
              ? null
              : isRequired
                ? 'added'
                : 'removed',
        };
      });
    },

    /** Les lignes: ce qui est exige, plus ce qui va cesser de l'etre. */
    visible() {
      return this.drafted.filter(
        (field) =>
          (field.isRequired || field.pending === 'removed') &&
          (this.step === null || field.step === this.step),
      );
    },

    /**
     * Les sections du catalogue, avec ce qu'elles exigent sur ce qu'elles
     * portent - brouillon compris, pour que le compte suive ce qu'on voit.
     *
     * Elles sont bâties sur le catalogue entier, pas sur les champs affiches:
     * une section dont aucun champ n'est obligatoire garde sa ligne et son
     * « 0/6 ». Sans cela elle disparaitrait de l'ecran, et on ne saurait meme
     * pas qu'il y a la des champs a exiger.
     */
    sections() {
      const bySection = [];

      this.drafted.forEach((field) => {
        let section = bySection.find((item) => item.step === field.step);

        if (!section) {
          section = {
            step: field.step,
            name: field.sectionLabel || `Section ${field.step}`,
            required: 0,
            total: 0,
          };
          bySection.push(section);
        }

        section.total += 1;

        if (field.isRequired) {
          section.required += 1;
        }
      });

      return bySection;
    },

    requiredCount() {
      return this.drafted.filter((field) => field.isRequired).length;
    },

    /** Les champs dont le brouillon s'ecarte de ce que porte la base. */
    pendingFields() {
      return this.drafted.filter((field) => field.pending);
    },

    isDirty() {
      return this.pendingFields.length > 0;
    },

    pendingLabel() {
      const count = this.pendingFields.length;

      return `${count} modification${count > 1 ? 's' : ''} non enregistrée${
        count > 1 ? 's' : ''
      }`;
    },
  },
  created() {
    this.load();
  },
  mounted() {
    // Fermer l'onglet ne passe par aucun garde de route: seul le navigateur
    // peut poser la question, et seulement si on la lui demande.
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';

      try {
        const fields = await surveyFieldService.getFields();

        this.fields = Array.isArray(fields) ? fields : [];
      } catch (err) {
        this.error =
          err.response?.data?.message ||
          'Les champs du questionnaire n’ont pas pu être chargés.';
      } finally {
        this.loading = false;
      }
    },

    /** L'etat d'un champ, brouillon d'abord, base ensuite. */
    requiredOf(field) {
      if (field.locked) {
        return true;
      }

      return field.key in this.draft ? this.draft[field.key] : field.isRequired;
    },

    /**
     * Note une intention.
     *
     * Revenir a l'etat enregistre efface l'entree plutot que d'en ecrire une
     * identique: sans cela « je retire, je reprends » laisserait la carte se
     * croire modifiee, et reclamer un enregistrement qui n'a rien a ecrire.
     */
    setDraft(field, isRequired) {
      const next = { ...this.draft };

      if (isRequired === field.isRequired) {
        delete next[field.key];
      } else {
        next[field.key] = isRequired;
      }

      this.draft = next;
    },

    selectSection(step) {
      this.step = step;
    },

    toggleCard() {
      this.open = !this.open;
    },

    /** Ouvrir la fenetre deplie la carte: on veut voir ce qu'on vient de regler. */
    openPicker() {
      this.pickerOpen = true;
      this.open = true;
    },

    /**
     * Le reglage demande depuis la fenetre.
     *
     * La vue de fond suit: si la section affichee cachait le champ qu'on vient
     * d'exiger, on bascule sur la sienne, pour que le resultat soit visible
     * des qu'on ferme.
     */
    toggleFromPicker(field) {
      const next = !this.requiredOf(field);

      this.setDraft(field, next);

      if (next && this.step !== null && this.step !== field.step) {
        this.step = field.step;
      }
    },

    removeField(field) {
      if (field.locked) {
        return;
      }

      this.setDraft(field, false);
    },

    /** Reprendre une ligne qu'on venait de retirer, sans tout abandonner. */
    undoField(field) {
      this.setDraft(field, field.isRequired);
    },

    discard() {
      this.draft = {};
    },

    /**
     * Ecrit le brouillon.
     *
     * Les champs partent ensemble mais sont ecrits un par un: l'API regle un
     * champ a la fois. Ce qui passe quitte le brouillon, ce qui echoue y reste
     * - on ne perd pas une intention parce qu'une autre a ete refusee, et le
     * compte qui subsiste dit exactement ce qu'il reste a reprendre.
     */
    async save() {
      if (!this.isDirty || this.savingAll) {
        return false;
      }

      this.savingAll = true;

      const changes = this.pendingFields.map((field) => ({
        key: field.key,
        isRequired: field.isRequired,
      }));

      const results = await Promise.allSettled(
        changes.map((change) =>
          surveyFieldService.setRequired(change.key, change.isRequired),
        ),
      );

      const draft = { ...this.draft };
      const failures = [];

      results.forEach((result, index) => {
        const { key } = changes[index];

        if (result.status === 'fulfilled') {
          const at = this.fields.findIndex((field) => field.key === key);

          if (at !== -1 && result.value) {
            this.fields.splice(at, 1, result.value);
          }

          delete draft[key];
          return;
        }

        failures.push(
          result.reason?.response?.data?.message ||
            `« ${key} » n’a pas pu être enregistré.`,
        );
      });

      this.draft = draft;
      this.savingAll = false;

      if (failures.length) {
        notificationService.toastError(failures[0]);
        return false;
      }

      notificationService.toastSuccess(
        'Les champs obligatoires ont été enregistrés.',
      );

      return true;
    },

    /**
     * La question posee au depart de la page: enregistrer, abandonner, ou
     * rester. La reduire a deux forcerait a choisir entre perdre le brouillon
     * et ne pas pouvoir revenir en arriere.
     *
     * @returns {Promise<boolean>} vrai si la navigation peut se poursuivre.
     */
    async confirmLeave() {
      if (!this.isDirty) {
        return true;
      }

      const result = await notificationService.confirm(
        'Modifications non enregistrées',
        `${this.pendingLabel} sur les champs obligatoires. Que souhaitez-vous faire ?`,
        {
          icon: 'warning',
          confirmText: 'Enregistrer',
          denyText: 'Abandonner',
          cancelText: 'Rester sur la page',
        },
      );

      if (result.isConfirmed) {
        // Un enregistrement qui echoue ne doit pas emporter la page avec lui:
        // on reste, le brouillon est intact et l'erreur est lisible.
        return this.save();
      }

      if (result.isDenied) {
        this.discard();
        return true;
      }

      return false;
    },

    onBeforeUnload(event) {
      if (!this.isDirty) {
        return;
      }

      event.preventDefault();
      // Les navigateurs affichent leur propre texte; seule la valeur compte.
      event.returnValue = '';
    },
  },
};
</script>

<template>
  <div class="list-card">
    <!-- L'en-tete d'une carte de liste, mais repliable: meme bande, memes
         classes, avec le titre rendu cliquable. -->
    <div
      class="list-card__header native-fields__header"
      :class="{ 'native-fields__header--open': open }"
    >
      <button
        type="button"
        class="native-fields__toggle"
        :aria-expanded="open ? 'true' : 'false'"
        data-testid="native-fields-toggle"
        @click="toggleCard"
      >
        <i
          class="native-fields__chevron"
          :class="open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
        ></i>
        <h2 class="list-card__title">Champs obligatoires du questionnaire</h2>
        <span v-if="!loading && fields.length" class="native-fields__summary">
          · {{ requiredCount }} sur {{ fields.length }}
        </span>
        <!-- Visible carte repliee: c'est la que le brouillon risquerait de se
             faire oublier. -->
        <span
          v-if="isDirty"
          class="native-fields__badge"
          data-testid="native-fields-badge"
        >
          {{ pendingLabel }}
        </span>
      </button>

      <div class="list-card__actions">
        <button
          type="button"
          class="list-btn list-btn--primary"
          :disabled="loading || !fields.length"
          data-testid="native-fields-add"
          @click="openPicker"
        >
          <i class="ri-checkbox-circle-line"></i>
          Marquer un champ obligatoire
        </button>
      </div>
    </div>

    <MarkRequiredFieldModal
      v-model="pickerOpen"
      :fields="drafted"
      :pending-label="isDirty ? pendingLabel : ''"
      @toggle="toggleFromPicker"
    />

    <template v-if="open">
      <p class="questions-note">
        Les champs standards que le questionnaire exige. Un champ verrouillé le
        reste — l’application ne sait pas s’en passer. Rien n’est appliqué avant
        d’avoir enregistré.
      </p>

      <p v-if="error" class="list-card__error" role="alert">{{ error }}</p>

      <p v-if="loading" class="list-card__state">Chargement des champs…</p>

      <div v-else-if="!fields.length" class="list-card__empty">
        <p class="list-card__empty-text">
          Aucun champ réglable pour le moment.
        </p>
      </div>

      <div
        v-else-if="!visible.length && step === null"
        class="list-card__empty"
      >
        <p class="list-card__empty-text">
          Aucun champ n’est obligatoire pour le moment.
        </p>
        <button
          type="button"
          class="list-card__empty-action"
          @click="openPicker"
        >
          Marquer un champ obligatoire
        </button>
      </div>

      <div v-else class="field-browser native-fields__browser">
        <nav
          class="field-browser__sections"
          aria-label="Sections du questionnaire"
        >
          <button
            type="button"
            class="field-browser__section"
            :class="{ 'field-browser__section--active': step === null }"
            data-testid="native-fields-section-all"
            @click="selectSection(null)"
          >
            <span>Toutes les sections</span>
            <span class="field-browser__section-count">
              {{ requiredCount }}/{{ fields.length }}
            </span>
          </button>

          <button
            v-for="section in sections"
            :key="section.step"
            type="button"
            class="field-browser__section"
            :class="{ 'field-browser__section--active': step === section.step }"
            :data-testid="`native-fields-section-${section.step}`"
            @click="selectSection(section.step)"
          >
            <span>{{ section.name }}</span>
            <span class="field-browser__section-count">
              {{ section.required }}/{{ section.total }}
            </span>
          </button>
        </nav>

        <div class="field-browser__panel">
          <p v-if="!visible.length" class="field-browser__empty">
            Aucun champ obligatoire dans cette section.
          </p>

          <ul v-else class="field-browser__list">
            <li
              v-for="field in visible"
              :key="field.key"
              class="field-browser__row"
              :class="{
                'native-fields__row--removed': field.pending === 'removed',
              }"
              :data-testid="`native-field-${field.key}`"
            >
              <span class="field-browser__text">
                <span class="field-browser__label">{{ field.label }}</span>
                <span class="field-browser__meta">
                  {{ field.sectionLabel }}
                  <template v-if="field.pending === 'added'">
                    · à enregistrer
                  </template>
                  <template v-else-if="field.pending === 'removed'">
                    · retiré, à enregistrer
                  </template>
                </span>
              </span>

              <span v-if="field.locked" class="field-browser__locked">
                <i class="ri-lock-line"></i>
                Verrouillé
              </span>

              <!-- Une ligne retiree reste visible jusqu'a l'enregistrement:
                   on peut la reprendre sans abandonner tout le brouillon. -->
              <button
                v-else-if="field.pending === 'removed'"
                type="button"
                class="row-action"
                :title="`Reprendre « ${field.label} »`"
                :data-testid="`native-field-undo-${field.key}`"
                @click="undoField(field)"
              >
                <i class="ri-arrow-go-back-line"></i>
                Reprendre
              </button>

              <button
                v-else
                type="button"
                class="row-action row-action--danger"
                :title="`Retirer « ${field.label} » des champs obligatoires`"
                :aria-label="`Retirer ${field.label} des champs obligatoires`"
                :data-testid="`native-field-remove-${field.key}`"
                @click="removeField(field)"
              >
                <i class="ri-indeterminate-circle-line"></i>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <!-- La barre reste visible carte repliee: un brouillon ne doit pas pouvoir
         se cacher derriere un pli. -->
    <footer
      v-if="isDirty"
      class="list-card__footer native-fields__save"
      data-testid="native-fields-save-bar"
    >
      <span class="native-fields__save-text">
        <i class="ri-information-line"></i>
        {{ pendingLabel }} — elles ne s’appliqueront qu’après enregistrement.
      </span>

      <span class="native-fields__save-actions">
        <button
          type="button"
          class="modal-btn modal-btn--ghost"
          :disabled="savingAll"
          data-testid="native-fields-discard"
          @click="discard"
        >
          Abandonner
        </button>
        <button
          type="button"
          class="modal-btn modal-btn--primary"
          :disabled="savingAll"
          data-testid="native-fields-save"
          @click="save"
        >
          {{ savingAll ? 'Enregistrement…' : 'Enregistrer' }}
        </button>
      </span>
    </footer>
  </div>
</template>

<style scoped>
/* La carte a ses marges, la fenetre a les siennes: seul l'ecart au bord
   change d'un endroit a l'autre. */
.native-fields__browser {
  padding: 4px 20px 16px;
}

/* Repliee, la carte n'est plus qu'une bande: son en-tete ne doit donc pas
   porter de filet en bas, sinon il souligne le vide. */
.native-fields__header {
  border-bottom-width: 0;
}

.native-fields__header--open {
  border-bottom-width: 1px;
}

/* Le titre devient la poignee: toute la bande de gauche est cliquable, pas
   seulement une fleche de 16px. */
.native-fields__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: left;
}

.native-fields__chevron {
  color: #6c757d;
  font-size: 18px;
  line-height: 1;
}

.native-fields__summary {
  font-size: 13px;
  color: #6c757d;
  white-space: nowrap;
}

/* L'ambre dit « en attente »: ni faute, ni succes. */
.native-fields__badge {
  padding: 2px 8px;
  border-radius: 10px;
  background: #fff6e5;
  border: 1px solid #f5d9a5;
  color: #8a5a00;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.native-fields__save {
  background: #fffaf1;
  border-top: 1px solid #f5d9a5;
}

.native-fields__save-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #8a5a00;
}

.native-fields__save-actions {
  display: inline-flex;
  gap: 8px;
}

/* Une ligne retiree s'efface sans disparaitre: on la reconnait, on peut la
   reprendre, et on voit ce que l'enregistrement va emporter. */
.native-fields__row--removed .field-browser__label {
  text-decoration: line-through;
  color: #6c757d;
}
</style>
